import React, { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import useClasses from '../../../hooks/useClasses';
import usePopup from '../../../hooks/usePopup';
import useRoutes from '../../../hooks/useRoutes';
import {
  Button,
  MessageTeaser,
  Pager,
  Popup,
  PopupBadge,
} from '../../../components';
import { ImmutableBadgeTaxonomyEntity } from '../../Taxonomy';
import { GenericViewProps, ImmutableMessageEntity } from '..';

import './style.scss';

interface Props extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entities: ImmutableMessageEntity[];
  pager: GenericViewProps['pager'];
  setCurrentPage: (currentPage: number) => void;
}

const ViewMessage: React.FC<Props> = ({
  entities,
  pager,
  setCurrentPage,
  className,
}) => {
  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('ViewMessage');
  const { badges } = useRoutes();

  const [popupText, setPopupText] = useState('');
  const [badge, setBadge] = useState<ImmutableBadgeTaxonomyEntity>();

  const popup = usePopup('message-popup', {
    closeOnOutsideClick: true,
    // we set closeOnRouteChange to false because the mechanism that opens this popup is
    // a corelink that adds a hash to the url. This causes the popup to close
    // since this is seen as a location change.
    //
    // We can either fine tune the implementation of the usePopup hook or add links
    // that act as buttons.
    // See https://uncinc.monday.com/boards/3375006290/pulses/5175880582
    closeOnRouteChange: false,
  });

  return (
    <div className={joinClasses(buildClass(), className)}>
      <ol className={buildClass('list')}>
        {entities.map((entity) => {
          const id: number = entity.getIn(['mid', 0, 'value'], null);

          return (
            <MessageTeaser
              key={`message:${id}`}
              entity={entity}
              onClick={() => {
                setBadge(entity.getIn(['field_badge', 0, 'entity']));
                setPopupText(entity.getIn(['text', 0, 'value', 1], ''));
                popup.open();
              }}
            />
          );
        })}
      </ol>

      {/*
        The pager expects the first page to be 1, however,
        the Drupal view gives us 0 as the first page.
        That's why we need to add 1 to the currentPage prop for the Pager and
        subtract 1 from the returned parameter in the setCurrentPage function.
      */}
      <Pager
        totalPages={pager.get('total_pages', 0)}
        currentPage={pager.get('current_page', 0) + 1}
        setCurrentPage={(page: number) => {
          setCurrentPage(page - 1);
          window.scrollTo(0, 0);
        }}
        marginPagesDisplayed={1}
        pageCount={2}
        pageRangeDisplayed={2}
      />

      {popup.isOpen && (
        <Popup
          title={t(`messages.${badge ? 'badge_popup' : 'old'}.title`)}
          closeButtonText={t('buttons.close')}
          cta={badge ? (
            <Button href={badges} secondary onClick={() => popup.close()}>
              {t('messages.badge_popup.cta')}
            </Button>
          ) : null}
          {...popup.popupProps}
        >
          {badge ? (
            <PopupBadge
              badge={badge}
            />
          ) : (
            <WysiwygField
              property="schema:text"
              html={popupText}
            />
          )}
        </Popup>
      )}
    </div>
  );
};

export default ViewMessage;
